import React, { useState } from 'react';
import { Award, CalendarDays, Check, CircleDollarSign, Layers, Pencil, Plus, Power, Tags, Trash2 } from 'lucide-react';
import * as hr from '../../api';
import { useHR } from '../../HRContext';
import Modal from '../../components/Modal';
import { DateInput, Select } from '../../components/Controls';
import { Chip, Kpi } from '../attendance/shared';
import { Empty, longDate } from './shared';

// Setup (leave types, grade allocations, public holidays at a glance), Leave Types and Holiday Setup — Stephanet's leavemgmt/Setup.jsx

const Tick = ({ v }) => (v ? <Check size={14} className="lvm-yes" /> : <span className="mds-dash">—</span>);
const holidayIn = (h, year) => (h.recurring ? `${year}${h.holiday_date.slice(4)}` : h.holiday_date);

export function Setup({ data, reload, go }) {
  const [grade, setGrade] = useState(null);
  const year = Number(data.today.slice(0, 4));
  const hols = data.holidays.filter(h => h.active !== false && h.holiday_type === 'Public Holiday');
  return (
    <div className="lvm-setup">
      <div className="mdr-card lvm-setup-card">
        <div className="lvm-setup-head"><div className="mds-card-title"><Tags size={16} /> Leave Types</div>
          {data.can.create && <button className="mds-run sm" onClick={() => go('types')}><Plus size={13} /> Add</button>}</div>
        <p>Configure available leave types</p>
        <div className="lvm-chips">{data.types.filter(t => t.active !== false).map(t => <span key={t.id} className="lvm-type">{t.name.toUpperCase()}</span>)}</div>
      </div>
      <div className="mdr-card lvm-setup-card">
        <div className="lvm-setup-head"><div className="mds-card-title"><Award size={16} /> Grade Allocations</div></div>
        <p>Days per grade per leave type — click a grade to edit</p>
        <div className="lvm-grades">
          {data.grades.map(gr => (
            <button key={gr.id} type="button" disabled={!data.can.edit} onClick={() => setGrade(gr)}>
              <b>{gr.name}</b><span>Annual {gr.Annual ?? '—'}d · Sick {gr.Sick ?? '—'}d · Casual {gr.Casual ?? '—'}d</span>{data.can.edit && <Pencil size={12} />}
            </button>
          ))}
          {!data.grades.length && <div className="mdr-empty">No pay grades set up. A grade appears here once it is on an employee's financial details.</div>}
        </div>
      </div>
      <div className="mdr-card lvm-setup-card">
        <div className="lvm-setup-head"><div className="mds-card-title"><CalendarDays size={16} /> Public Holidays</div>
          {data.can.create && <button className="mds-run sm" onClick={() => go('holidays')}><Plus size={13} /> Add</button>}</div>
        <p>Nigerian public holidays</p>
        <ul className="lvm-hols">{hols.map(h => <li key={h.id}><b>{h.name}</b><span>{longDate(holidayIn(h, year))}</span></li>)}</ul>
      </div>
      {grade && <GradeModal grade={grade} types={data.entitlement_types} onClose={() => setGrade(null)} onDone={() => { setGrade(null); reload(); }} />}
    </div>
  );
}

function GradeModal({ grade, types, onClose, onDone }) {
  const { notify } = useHR();
  const [f, setF] = useState(() => Object.fromEntries(types.map(t => [t, grade[t] ?? ''])));
  const [busy, setBusy] = useState(false);
  const save = e => {
    e.preventDefault();
    setBusy(true);
    const days = Object.fromEntries(Object.entries(f).map(([t, v]) => [t, v === '' ? null : Number(v)]));
    hr.saveGradeAllocation({ pay_grade: grade.name, days }).then(r => { notify(`${grade.name}: ${r.changes} allocation(s) updated`); onDone(); })
      .catch(err => notify(err.message, 'bad')).finally(() => setBusy(false));
  };
  return (
    <Modal className="mdl-modal" title={<><Award size={18} /> Grade Allocation — {grade.name}</>} onClose={() => !busy && onClose()}>
      <form className="mdl-modal-body" onSubmit={save}>
        <p className="mdl-modal-lead">Leave days a year for employees on <b>{grade.name}</b>. An employee's own figure, where set, takes precedence; blank uses the leave type default.</p>
        <div className="lvm-grid two">
          {types.map(t => <label key={t} className="mdl-field">{t} Leave (days)<input type="number" min="0" max="366" value={f[t]} onChange={e => setF(x => ({ ...x, [t]: e.target.value }))} placeholder="Default" /></label>)}
        </div>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={busy}><Check size={14} /> {busy ? 'Saving…' : 'Save Allocation'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- Leave Types
const TYPE_BLANK = { name: '', description: '', credit_type: 'Yearly', days: '', has_balance: true, attachment: 'Optional', paid: true, mgr_apply: true,
  emp_apply: true, usage: 'Can Be Split', gender: 'All', max_carry_forward: 0, active: true };

export function LeaveTypes({ data, reload, tick }) {
  const { notify } = useHR();
  const [editing, setEditing] = useState(null);
  const types = data.types;
  const toggle = t => hr.saveLeaveType(t.id, { active: t.active === false })
    .then(() => { notify(`${t.name} leave ${t.active === false ? 'activated' : 'deactivated'}`); reload(); }).catch(e => notify(e.message, 'bad'));
  return (
    <>
      <div className="lvm-toolbar">
        <div className="md-kpis mds-kpis lvm-kpis" key={`t${tick}`}>
          <Kpi label="Total Types" value={types.length} Ico={Layers} tone="blue" />
          <Kpi label="Active" value={types.filter(t => t.active !== false).length} Ico={Power} tone="green" i={1} />
          <Kpi label="Paid" value={types.filter(t => t.paid !== false).length} Ico={CircleDollarSign} tone="teal" i={2} />
          <Kpi label="Unpaid" value={types.filter(t => t.paid === false).length} Ico={CircleDollarSign} tone="orange" i={3} />
        </div>
        {data.can.create && <button className="mds-run no-print" onClick={() => setEditing({})}><Plus size={14} /> Add Leave Type</button>}
      </div>
      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>{data.can.edit && <th className="c no-print">Action</th>}<th>Credit ID</th><th>Leave Type</th><th>Credit Type</th><th className="c">Days</th><th className="c">Balance</th>
              <th>Attachment</th><th>Payment</th><th className="c">Mgr Apply</th><th className="c">Emp Apply</th><th>Usage Type</th><th>Applies To</th><th>Status</th></tr></thead>
            <tbody key={tick}>
              {types.map((t, i) => (
                <tr key={t.id} className={`mdr-row ${t.active === false ? 'lvm-off' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  {data.can.edit && <td className="c no-print nowrap">
                    <button className="mds-view" onClick={() => setEditing(t)}><Pencil size={12} /> Edit</button>
                    <button className={`lvm-icon ${t.active === false ? 'ok' : 'bad'}`} title={t.active === false ? 'Activate' : 'Deactivate'} onClick={() => toggle(t)}><Power size={13} /></button>
                  </td>}
                  <td className="mdr-id">{t.code}</td>
                  <td className="lvm-emp"><b>{t.name} Leave</b><small>{t.description}</small></td>
                  <td>{t.credit_type}</td><td className="c b">{t.days || '—'}</td><td className="c"><Tick v={t.has_balance} /></td>
                  <td><Chip tone={t.attachment === 'Required' ? 'orange' : 'gray'}>{t.attachment === 'Required' ? 'Req.' : 'Opt.'}</Chip></td>
                  <td><Chip tone={t.paid === false ? 'red' : 'green'}>{t.paid === false ? 'Unpaid' : 'Paid'}</Chip></td>
                  <td className="c"><Tick v={t.mgr_apply} /></td><td className="c"><Tick v={t.emp_apply} /></td>
                  <td className="nowrap">{t.usage}</td><td>{t.gender || 'All'}</td>
                  <td><Chip tone={t.active === false ? 'gray' : 'green'}>{t.active === false ? 'Inactive' : 'Active'}</Chip></td>
                </tr>
              ))}
              {!types.length && <Empty cols={13}>No leave types.</Empty>}
            </tbody>
          </table>
        </div>
      </div>
      {editing && <TypeModal type={editing} data={data} onClose={() => setEditing(null)} onDone={() => { setEditing(null); reload(); }} />}
    </>
  );
}

function TypeModal({ type, data, onClose, onDone }) {
  const { notify } = useHR();
  const isNew = !type.id;
  const [f, setF] = useState(() => ({ ...TYPE_BLANK, ...(isNew ? {} : type), days: type.days ?? '' }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = e => {
    e.preventDefault();
    setBusy(true);
    const body = { ...Object.fromEntries(Object.keys(TYPE_BLANK).map(k => [k, f[k]])), days: f.days === '' ? 0 : Number(f.days),
      max_carry_forward: Number(f.max_carry_forward) || 0, description: f.description || null };
    hr.saveLeaveType(isNew ? null : type.id, body)
      .then(() => { notify(`${f.name} leave ${isNew ? 'added' : 'saved'}`); onDone(); }).catch(err => notify(err.message, 'bad')).finally(() => setBusy(false));
  };
  const Toggle = ({ k, label }) => <label className="lvm-check"><input type="checkbox" checked={!!f[k]} onChange={e => set(k, e.target.checked)} /> {label}</label>;
  return (
    <Modal className="mdl-modal lvm-wide" wide title={<><Tags size={18} /> {isNew ? 'Add Leave Type' : `Edit ${type.name} Leave`}</>} onClose={() => !busy && onClose()}>
      <form className="mdl-modal-body" onSubmit={save}>
        <div className="lvm-grid">
          <label className="mdl-field">Leave Type *
            <input value={f.name} onChange={e => set('name', e.target.value)} required placeholder="e.g. Offshore Rotation" />
          </label>
          <label className="mdl-field span2">Description<input value={f.description || ''} onChange={e => set('description', e.target.value)} /></label>
          <label className="mdl-field">Credit Type<Select value={f.credit_type} onChange={e => set('credit_type', e.target.value)}>{['Yearly', 'Quarterly', 'Monthly', 'One-Time', 'Manual Allocation'].map(x => <option key={x} value={x}>{x}</option>)}</Select></label>
          <label className="mdl-field">Days<input type="number" min="0" value={f.days} onChange={e => set('days', e.target.value)} /></label>
          <label className="mdl-field">Max Carry Forward<input type="number" min="0" value={f.max_carry_forward} onChange={e => set('max_carry_forward', e.target.value)} /></label>
          <label className="mdl-field">Attachment<Select value={f.attachment} onChange={e => set('attachment', e.target.value)}><option value="Optional">Optional</option><option value="Required">Required</option></Select></label>
          <label className="mdl-field">Usage Type<Select value={f.usage} onChange={e => set('usage', e.target.value)}><option value="Can Be Split">Can Be Split</option><option value="Wholly Once">Wholly Once</option></Select></label>
          <label className="mdl-field">Applies To<Select value={f.gender} onChange={e => set('gender', e.target.value)}><option value="All">All</option><option value="Female">Female</option><option value="Male">Male</option></Select></label>
        </div>
        <div className="lvm-toggles">
          <Toggle k="has_balance" label="Tracks balance" /><Toggle k="paid" label="Paid" /><Toggle k="mgr_apply" label="Manager can apply" />
          <Toggle k="emp_apply" label="Employee can apply" /><Toggle k="active" label="Active" />
        </div>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={busy || !f.name.trim()}><Check size={14} /> {busy ? 'Saving…' : isNew ? 'Add Leave Type' : 'Save Changes'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- Holiday Setup
const HOL_BLANK = { holiday_date: '', name: '', holiday_type: 'Public Holiday', state_id: '', recurring: false, paid: true, active: true };
const HOL_TONE = { 'Public Holiday': 'blue', 'Religious Holiday': 'purple', 'Company Holiday': 'teal' };

export function Holidays({ data, reload, tick }) {
  const { notify } = useHR();
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);
  const year = Number(data.today.slice(0, 4));
  const list = [...data.holidays].sort((a, b) => holidayIn(a, year).localeCompare(holidayIn(b, year)));
  const del = h => hr.deleteHoliday(h.id).then(() => { notify(`${h.name} removed`); setRemoving(null); reload(); }).catch(e => notify(e.message, 'bad'));
  return (
    <>
      <div className="lvm-toolbar">
        <div className="md-kpis mds-kpis lvm-kpis" key={`h${tick}`}>
          <Kpi label="Total Holidays" value={list.length} Ico={CalendarDays} tone="blue" />
          <Kpi label="Public" value={list.filter(h => h.holiday_type === 'Public Holiday').length} Ico={CalendarDays} tone="green" i={1} />
          <Kpi label="Religious" value={list.filter(h => h.holiday_type === 'Religious Holiday').length} Ico={CalendarDays} tone="purple" i={2} />
          <Kpi label="Company" value={list.filter(h => h.holiday_type === 'Company Holiday').length} Ico={CalendarDays} tone="teal" i={3} />
        </div>
        {data.can.create && <button className="mds-run no-print" onClick={() => setEditing({})}><Plus size={14} /> Add Holiday</button>}
      </div>
      <div className="mdr-card mdr-table-card">
        <div className="mdr-scroll">
          <table className="mdr-table mds-table">
            <thead><tr>{data.can.edit && <th className="c no-print">Action</th>}<th>Holiday ID</th><th>Date</th><th>Holiday Name</th><th>Type</th><th>State</th>
              <th className="c">Recurring</th><th className="c">Paid</th><th>Status</th></tr></thead>
            <tbody key={tick}>
              {list.map((h, i) => (
                <tr key={h.id} className={`mdr-row ${h.active === false ? 'lvm-off' : ''}`} style={{ animationDelay: `${Math.min(i, 20) * 15}ms` }}>
                  {data.can.edit && <td className="c no-print nowrap">
                    <button className="mds-view" onClick={() => setEditing(h)}><Pencil size={12} /> Edit</button>
                    {data.can.delete && <button className="lvm-icon bad" title="Delete" onClick={() => setRemoving(h)}><Trash2 size={13} /></button>}
                  </td>}
                  <td className="mdr-id">{h.code}</td><td className="nowrap b">{longDate(holidayIn(h, year))}</td><td>{h.name}</td>
                  <td><Chip tone={HOL_TONE[h.holiday_type]}>{h.holiday_type}</Chip></td><td>{h.state || 'All'}</td>
                  <td className="c"><Tick v={h.recurring} /></td><td className="c"><Tick v={h.paid} /></td>
                  <td><Chip tone={h.active === false ? 'gray' : 'green'}>{h.active === false ? 'Inactive' : 'Active'}</Chip></td>
                </tr>
              ))}
              {!list.length && <Empty cols={9}>No holidays set up.</Empty>}
            </tbody>
          </table>
        </div>
        <div className="mdr-foot att2-foot"><span>Recurring holidays repeat on the same date every year. Holidays are excluded from leave working days.</span></div>
      </div>
      {editing && <HolidayModal h={editing} states={data.states} onClose={() => setEditing(null)} onDone={() => { setEditing(null); reload(); }} />}
      {removing && (
        <Modal className="mdl-modal" title={<><Trash2 size={18} /> Delete Holiday</>} onClose={() => setRemoving(null)}>
          <div className="mdl-modal-body">
            <p className="mdl-modal-lead">Delete <b>{removing.name}</b>? Leave already approved over that day keeps its day count.</p>
            <div className="mdh-form-foot"><button className="mdr-btn" onClick={() => setRemoving(null)}>Cancel</button>
              <button className="mds-run att2-danger" onClick={() => del(removing)}><Trash2 size={14} /> Delete</button></div>
          </div>
        </Modal>
      )}
    </>
  );
}

function HolidayModal({ h, states, onClose, onDone }) {
  const { notify } = useHR();
  const isNew = !h.id;
  const [f, setF] = useState(() => ({ ...HOL_BLANK, ...(isNew ? {} : h), state_id: h.state_id || '' }));
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const save = e => {
    e.preventDefault();
    setBusy(true);
    const body = { ...Object.fromEntries(Object.keys(HOL_BLANK).map(k => [k, f[k]])), state_id: f.state_id || null };
    hr.saveHoliday(isNew ? null : h.id, body)
      .then(() => { notify(`${f.name} ${isNew ? 'added' : 'saved'}`); onDone(); }).catch(err => notify(err.message, 'bad')).finally(() => setBusy(false));
  };
  return (
    <Modal className="mdl-modal" title={<><CalendarDays size={18} /> {isNew ? 'Add Holiday' : `Edit ${h.name}`}</>} onClose={() => !busy && onClose()}>
      <form className="mdl-modal-body" onSubmit={save}>
        <div className="lvm-grid two">
          <label className="mdl-field">Date *<DateInput value={f.holiday_date} onChange={e => set('holiday_date', e.target.value)} required /></label>
          <label className="mdl-field">Holiday Name *<input value={f.name} onChange={e => set('name', e.target.value)} required /></label>
          <label className="mdl-field">Type<Select value={f.holiday_type} onChange={e => set('holiday_type', e.target.value)}>
            {['Public Holiday', 'Religious Holiday', 'Company Holiday'].map(x => <option key={x} value={x}>{x}</option>)}</Select></label>
          <label className="mdl-field">State<Select value={f.state_id} onChange={e => set('state_id', e.target.value)}>
            <option value="">All</option>{(states || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</Select></label>
        </div>
        <div className="lvm-toggles">
          {[['recurring', 'Recurring yearly'], ['paid', 'Paid'], ['active', 'Active']].map(([k, l]) => <label key={k} className="lvm-check"><input type="checkbox" checked={!!f[k]} onChange={e => set(k, e.target.checked)} /> {l}</label>)}
        </div>
        <div className="mdh-form-foot">
          <button type="button" className="mdr-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button className="mds-run" disabled={busy}><Check size={14} /> {busy ? 'Saving…' : isNew ? 'Add Holiday' : 'Save Changes'}</button>
        </div>
      </form>
    </Modal>
  );
}
